/* eslint-disable @typescript-eslint/no-floating-promises */
import { useState } from 'react';
import {
  Autocomplete,
  Grid,
  InputAdornment,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery, useLazyQuery } from '@apollo/client';
import { GET_MEMBERS, GET_PROJECT_NAMES } from '../gql';

interface IProps {
  setResourceData: (
    value: {
      displayName: string;
      id: string;
    } | null
  ) => void;
  setProjectData: (
    value: {
      projectName: string;
      id: string;
    } | null
  ) => void;
  setProjectManagerData: (
    value: {
      displayName: string;
      id: string;
    } | null
  ) => void;
  setFlaggedData: (flagged: boolean) => void;
}

function NotificationFilter({
  setResourceData,
  setProjectData,
  setProjectManagerData,
  setFlaggedData
}: IProps): JSX.Element {
  const [resource, setResource] = useState<{
    displayName: string;
    id: string;
  } | null>(null);
  const [project, setProject] = useState<{
    projectName: string;
    id: string;
  } | null>(null);
  const [projectManager, setProjectManager] = useState<{
    displayName: string;
    id: string;
  } | null>(null);
  const [flagged, setFlagged] = useState(false);

  const { data } = useQuery(GET_PROJECT_NAMES);
  const projects: Array<{ projectName: string; id: string }> =
    data?.getProjects ?? [];

  const [
    getMembers,
    { loading: loadingMembers, error: membersError, data: membersData }
  ] = useLazyQuery(GET_MEMBERS);
  const members = membersData?.getMembers.data || [];

  return (
    <Grid
      container
      spacing={2}
      sx={{
        padding: 0
      }}
    >
      <Grid item xs={12} sm={6} lg={3}>
        <Autocomplete
          value={resource}
          onInputChange={(event, newInputValue) => {
            if (newInputValue !== '') {
              getMembers({ variables: { displayName: newInputValue } });
            }
          }}
          options={loadingMembers || membersError ? ['Loading...'] : members}
          getOptionLabel={(option) => option.displayName}
          onChange={(event, newValue) => {
            setResource(newValue);
            setResourceData(newValue);
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              maxHeight: '48px',
              fontSize: '13px',
              '& .MuiOutlinedInput-input::placeholder': {
                fontFamily: 'Be Vietnam Pro',
                fontSize: '16px',
                fontWeight: '500',
                color: '#5F6368',
                opacity: 1
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Resource"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Autocomplete
          value={project}
          onChange={(event, newValue) => {
            setProject(newValue);
            setProjectData(newValue);
          }}
          options={projects}
          getOptionLabel={(option) => option.projectName}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              maxHeight: '48px',
              fontSize: '13px',
              '& .MuiOutlinedInput-input::placeholder': {
                fontFamily: 'Be Vietnam Pro',
                fontSize: '16px',
                fontWeight: '500',
                color: '#5F6368',
                opacity: 1
              }
            }
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Filter By Project Name" />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Autocomplete
          value={projectManager}
          onInputChange={(event, newInputValue) => {
            if (newInputValue !== '') {
              getMembers({ variables: { displayName: newInputValue } });
            }
          }}
          options={loadingMembers || membersError ? ['Loading...'] : members}
          getOptionLabel={(option) => option.displayName}
          onChange={(event, newValue) => {
            setProjectManager(newValue);
            setProjectManagerData(newValue);
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              maxHeight: '48px',
              fontSize: '13px',
              '& .MuiOutlinedInput-input::placeholder': {
                fontFamily: 'Be Vietnam Pro',
                fontSize: '16px',
                fontWeight: '500',
                color: '#5F6368',
                opacity: 1
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Project Manager/Delivery Responsible"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={flagged}
              onChange={(e) => {
                setFlagged(e.target.checked);
                setFlaggedData(e.target.checked);
              }}
              sx={{
                color: '#5F6368',
                '&.Mui-checked': {
                  color: '#5F6368'
                }
              }}
            />
          }
          label="Flag Raised"
          sx={{
            alignItems: 'center',
            height: '48px',
            borderRadius: '8px',
            border: '1px solid rgba(95, 99, 104, 0.5)',
            width: '100%',
            padding: '0 10px',
            margin: 0,

            '& .MuiTypography-root': {
              fontSize: '16px',
              fontWeight: '500',
              color: '#5F6368',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        />
      </Grid>
    </Grid>
  );
}

export default NotificationFilter;
